/*
 * Frost — reusable product tile behavior (see snippets/frost-product-card.liquid).
 * Deliberately its own copy, not shared with assets/orizza-product-card.js.
 * The desktop hover crossfade between the two product photos is pure CSS
 * (see frost-product-card.css, :hover rules under [data-frost-pc-swipe]).
 * Touch devices have no hover, so here we:
 *   1. Let a left/right swipe on the photo toggle between the two images.
 *   2. Let a tap on either dot jump straight to that image.
 * Both just flip the same `is-alt-active` class the CSS already uses, and
 * keep the two dots (mobile-only) in sync with whichever image is showing.
 * Cards with only one photo don't get `[data-frost-pc-swipe]` at all, so
 * this does nothing for them.
 * Also drives the tag badges' slide-in / loop (see initBadges below).
 */
(function () {
  function initCard(card) {
    if (card.dataset.frostPcInit) return;
    card.dataset.frostPcInit = 'true';

    var media = card.querySelector('.frost-pc__media');
    if (!media) return;

    var dots = media.querySelectorAll('[data-frost-pc-dot]');

    function setActive(showAlt) {
      media.classList.toggle('is-alt-active', showAlt);
      dots.forEach(function (dot) {
        var isAltDot = dot.getAttribute('data-frost-pc-dot') === '1';
        dot.classList.toggle('is-active', isAltDot === showAlt);
      });
    }

    dots.forEach(function (dot) {
      dot.addEventListener('click', function (event) {
        // The whole card is a link to the product — a dot tap should
        // switch photos, not navigate.
        event.preventDefault();
        event.stopPropagation();
        setActive(dot.getAttribute('data-frost-pc-dot') === '1');
      });
    });

    var startX = null;
    var startY = null;

    media.addEventListener(
      'touchstart',
      function (event) {
        var touch = event.touches[0];
        startX = touch.clientX;
        startY = touch.clientY;
      },
      { passive: true }
    );

    media.addEventListener(
      'touchend',
      function (event) {
        if (startX === null) return;
        var touch = event.changedTouches[0];
        var dx = touch.clientX - startX;
        var dy = touch.clientY - startY;
        startX = null;
        startY = null;

        // Ignore short taps and mostly-vertical drags (those are just the
        // page scrolling past the card).
        if (Math.abs(dx) < 30 || Math.abs(dx) < Math.abs(dy)) return;

        var showingAlt = media.classList.contains('is-alt-active');
        if (dx < 0 && !showingAlt) {
          setActive(true); // swiped left -> show the second photo
        } else if (dx > 0 && showingAlt) {
          setActive(false); // swiped right -> back to the first photo
        }
      },
      { passive: true }
    );
  }

  /* ---------------------------------------------------------------------
     Badges: slide in from the photo's left edge at their natural width.
     One badge slides in each time the card comes into view; several loop —
     slide in, hold, slide back out, next one in. Paused (and reset) while
     the card is off screen, so coming back replays it from the first.
  --------------------------------------------------------------------- */
  var BADGE_HOLD_MS = 4000;
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var badgeObserver = null;

  function initBadges(wrap) {
    if (wrap.dataset.frostPcBadgesInit) return;
    wrap.dataset.frostPcBadgesInit = 'true';
    if (reduceMotion || !('IntersectionObserver' in window)) return;

    var badges = Array.prototype.slice.call(wrap.querySelectorAll('.frost-pc__badge'));
    if (!badges.length) return;

    wrap.classList.add('is-anim');
    var index = 0;
    var timer = null;

    function show(i) {
      badges.forEach(function (badge, n) { badge.classList.toggle('is-active', n === i); });
    }

    wrap._frostStart = function () {
      if (timer || wrap._frostRunning) return;
      wrap._frostRunning = true;
      index = 0;
      // Let the parked (off-edge) state paint first so the first slide-in animates.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          if (wrap._frostRunning) show(index);
        });
      });
      if (badges.length > 1) {
        timer = setInterval(function () {
          index = (index + 1) % badges.length;
          show(index);
        }, BADGE_HOLD_MS);
      }
    };

    wrap._frostStop = function () {
      clearInterval(timer);
      timer = null;
      wrap._frostRunning = false;
      show(-1);
    };

    // Watch the photo, not the badge stack: the stack itself has no width
    // (its badges are absolutely positioned), which IntersectionObserver
    // can't measure reliably.
    var target = wrap.closest('.frost-pc__media') || wrap;
    target._frostBadges = wrap;
    if (!badgeObserver) {
      badgeObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var badgesWrap = entry.target._frostBadges;
          if (entry.intersectionRatio >= 0.3) badgesWrap._frostStart();
          else if (!entry.isIntersecting) badgesWrap._frostStop();
        });
      }, { threshold: [0, 0.3] });
    }
    badgeObserver.observe(target);
  }

  function setup() {
    document.querySelectorAll('[data-frost-pc-swipe]').forEach(initCard);
    document.querySelectorAll('[data-frost-pc-badges]').forEach(initBadges);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }

  // Cards inserted after this script's own DOMContentLoaded handler already
  // ran (e.g. cards revealed by a tab switch) won't have been picked up by
  // that initial scan. initCard() is idempotent, so callers can just re-run
  // this against the whole document at any time.
  window.FrostProductCard = { init: setup };
})();
